import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/types/database';
import {
  LayoutDashboard,
  Laptop,
  Receipt,
  Wrench,
  X,
  Package,
  ShoppingBag,
  RotateCcw,
  Users,
  Building2,
  ShieldCheck,
  DollarSign,
  BarChart3,
  History,
  Settings,
  ScanLine,
  LogOut,
} from 'lucide-react';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  roles: UserRole[];
}

const navItems: NavItem[] = [
  { name: 'Dashboard', path: '/app', icon: LayoutDashboard, roles: ['owner', 'manager', 'staff'] },
  { name: 'Laptops', path: '/app/inventory/laptops', icon: Laptop, roles: ['owner', 'manager', 'staff'] },
  { name: 'Accessories', path: '/app/inventory/accessories', icon: Package, roles: ['owner', 'manager', 'staff'] },
  { name: 'Sales & Invoices', path: '/app/sales', icon: Receipt, roles: ['owner', 'manager', 'staff'] },
  { name: 'Repairs Kanban', path: '/app/repairs', icon: Wrench, roles: ['owner', 'manager', 'staff'] },
  { name: 'Warranties', path: '/app/warranties', icon: ShieldCheck, roles: ['owner', 'manager', 'staff'] },
  { name: 'Customers', path: '/app/customers', icon: Users, roles: ['owner', 'manager', 'staff'] },
  { name: 'Purchases', path: '/app/purchases', icon: ShoppingBag, roles: ['owner', 'manager'] },
  { name: 'Returns', path: '/app/returns', icon: RotateCcw, roles: ['owner', 'manager'] },
  { name: 'Suppliers', path: '/app/suppliers', icon: Building2, roles: ['owner', 'manager'] },
  { name: 'Expenses', path: '/app/expenses', icon: DollarSign, roles: ['owner', 'manager'] },
  { name: 'Reports', path: '/app/reports', icon: BarChart3, roles: ['owner', 'manager'] },
  { name: 'Audit Log', path: '/app/audit', icon: History, roles: ['owner'] },
  { name: 'Scanner', path: '/app/scanner', icon: ScanLine, roles: ['owner', 'manager', 'staff'] },
  { name: 'Settings', path: '/app/settings', icon: Settings, roles: ['owner', 'manager'] },
];

export const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const { role, profile, signOut } = useAuth();

  const filteredItems = navItems.filter((item) => role && item.roles.includes(role));

  const bottomItems = [
    { name: 'Home', path: '/app', icon: LayoutDashboard },
    { name: 'Laptops', path: '/app/inventory/laptops', icon: Laptop },
    { name: 'Sales', path: '/app/sales', icon: Receipt },
    { name: 'Repairs', path: '/app/repairs', icon: Wrench },
  ];

  return (
    <>
      {/* Slide-over Menu Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
            onClick={onClose}
          />

          {/* Drawer Content */}
          <div className="relative w-4/5 max-w-xs bg-brand-black text-white flex flex-col h-full z-10 shadow-xl">
            <div className="p-4 border-b border-neutral-800 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img
                  src="/reference/al-kuwait-tech-logo.png"
                  alt="Al Kuwait Tech Logo"
                  className="h-8 w-auto bg-white rounded p-0.5"
                />
                <span className="font-bold text-xs">AL KUWAIT TECH</span>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-neutral-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Profile Info */}
            <div className="p-3 bg-neutral-900 border-b border-neutral-800 flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-brand-green flex items-center justify-center font-bold text-xs text-white">
                {profile?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="overflow-hidden">
                <p className="text-xs font-bold text-white truncate">{profile?.full_name || 'Staff User'}</p>
                <p className="text-[10px] text-emerald-400 uppercase font-semibold">{role || 'staff'}</p>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {filteredItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/app'}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-brand-green text-white shadow-xs'
                          : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{item.name}</span>
                  </NavLink>
                );
              })}
            </div>

            {/* Drawer Logout Footer */}
            <div className="p-3 border-t border-neutral-800">
              <button
                onClick={() => {
                  onClose();
                  signOut();
                }}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-brand-red text-white text-xs font-medium rounded-md hover:bg-brand-red-hover"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-brand-border z-40 px-2 py-1 flex justify-around items-center shadow-lg">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/app'}
              className={({ isActive }) =>
                `flex flex-col items-center py-1 px-3 rounded-md text-xs font-medium transition-colors ${
                  isActive ? 'text-brand-green' : 'text-brand-muted hover:text-brand-black'
                }`
              }
            >
              <Icon className="w-5 h-5 mb-0.5" />
              <span className="text-[10px]">{item.name}</span>
            </NavLink>
          );
        })}
      </nav>
    </>
  );
};
